import type {
  AgentTranscript,
  ToolResultBlock,
  TranscriptCall,
  TranscriptContentBlock,
  TranscriptMessage,
} from '~/entities/document-agent'
import { pluralize } from '~/shared/lib/pluralize'
import { MATCH_CRITERIA_LABELS } from '../config'
import { isToolResultBlock } from './transcript'

export type StepTone = 'success' | 'error' | undefined

/**
 * One thing the agent did: a tool request and the answer it got, or a message. Built from the
 * transcript, so every step can still show the raw request and response.
 */
export type AgentStep = {
  id: string
  kind: 'document' | 'tool' | 'message' | 'nudge'
  icon: string
  /** What was asked, in words. */
  request: string
  /** What came back, in words. */
  response: string | null
  tone: StepTone
  /** Time since the session started, after the model call that made the step. */
  elapsedMs: number | null
  toolName?: string
  input?: unknown
  result?: ToolResultBlock
  /** The model's thinking and text in the same reply, shown with the step. */
  thinking?: string
  text?: string
  /** Pages the step read, to open in the document pane. */
  pages?: number[]
  /** A search the step ran, to find in the document text. */
  searchQuery?: string
  call?: TranscriptCall
  /** The message's own blocks, for steps that are a whole message. */
  blocks?: TranscriptContentBlock[]
}

type Input = Record<string, unknown>

function blocksOf(message: TranscriptMessage): TranscriptContentBlock[] {
  if (typeof message.content === 'string') return [{ type: 'text', text: message.content }]
  return Array.isArray(message.content) ? message.content : []
}

export function resultText(result: ToolResultBlock | undefined): string {
  const content = result?.content
  if (!content) return ''
  if (typeof content === 'string') return content
  return content
    .map((item) => (item.type === 'text' ? (item.text ?? '') : ''))
    .filter(Boolean)
    .join('\n')
}

/** The first line that says something: a lead-in ending in ":" gives way to what follows. */
function firstLine(text: string, max = 140): string {
  const lines = text
    .trim()
    .split('\n')
    .map((value) => value.replace(/^[\s\-*•]+/, '').trim())
    .filter(Boolean)
  const line = lines.find((value) => !value.endsWith(':')) ?? lines[0] ?? ''
  return line.length > max ? `${line.slice(0, max - 1)}…` : line
}

function asInput(value: unknown): Input {
  return value && typeof value === 'object' ? (value as Input) : {}
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function humanize(value: unknown): string | null {
  const text = str(value)
  return text ? text.replace(/_/g, ' ') : null
}

function pageRange(start: number, end: number): number[] {
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return []
  return Array.from({ length: Math.min(end - start + 1, 50) }, (_, index) => start + index)
}

function formatPages(pages: number[]): string {
  if (pages.length === 0) return ''
  if (pages.length === 1) return `page ${pages[0]}`
  const contiguous = pages.every((page, index) => index === 0 || page === pages[index - 1] + 1)
  return contiguous
    ? `pages ${pages[0]}–${pages[pages.length - 1]}`
    : `pages ${pages.slice(0, 6).join(', ')}${pages.length > 6 ? '…' : ''}`
}

function countCodes(input: Input): number {
  return ['carc_codes', 'rarc_codes', 'natera_tags'].reduce(
    (sum, key) => sum + (Array.isArray(input[key]) ? (input[key] as unknown[]).length : 0),
    0,
  )
}

function describePick(side: string, pick: unknown): string | null {
  const value = asInput(pick)
  const decision = str(value.decision)
  if (!decision) return null
  const candidate = str(value.candidate)
  const confidence = str(value.confidence)
  return `${side}: ${[decision, candidate].filter(Boolean).join(' ')}${confidence ? ` (${confidence})` : ''}`
}

/** Request and response in words, by tool. Unknown tools fall back to their name. */
function describeTool(
  name: string,
  input: Input,
  result: ToolResultBlock | undefined,
): Pick<AgentStep, 'icon' | 'request' | 'response' | 'tone' | 'pages' | 'searchQuery'> {
  const text = resultText(result)
  const isError = !!result?.is_error
  const errorResponse = isError ? `Rejected by validation: ${firstLine(text) || 'error'}` : null
  const tone: StepTone = isError ? 'error' : undefined

  switch (name) {
    case 'get_pages': {
      const requested = pageRange(Number(input.start), Number(input.end ?? input.start))
      const returnedPages = [...text.matchAll(/<!--\s*page:\s*(\d+)\s*-->/g)].map((match) =>
        Number(match[1]),
      )
      const returned = returnedPages.length
      // Link the pages that came back; the request can ask for more than the tool returns.
      const pages = returned ? returnedPages : isError ? [] : requested
      return {
        icon: 'description',
        request: `Read ${formatPages(requested) || 'pages'}`,
        response:
          errorResponse ??
          (returned ? `${returned} ${pluralize(returned, 'page')} of text` : firstLine(text)),
        tone,
        pages,
      }
    }
    case 'search_document': {
      const query = str(input.query) ?? ''
      const hits = text.split('\n').filter((line) => /^page \d+:/i.test(line.trim()))
      const pages = [
        ...new Set(hits.map((line) => Number(/^page (\d+):/i.exec(line.trim())?.[1]))),
      ].filter(Number.isFinite)
      return {
        icon: 'search',
        request: `Searched the document for “${query}”${input.regex ? ' (regex)' : ''}`,
        response:
          errorResponse ??
          (hits.length
            ? `${hits.length} ${pluralize(hits.length, 'match', 'es')} on ${formatPages(pages)}`
            : 'No matches'),
        tone,
        pages,
        searchQuery: input.regex ? undefined : query,
      }
    }
    case 'get_guide': {
      const guide = [humanize(input.phase), humanize(input.outcome)].filter(Boolean).join(' · ')
      return {
        icon: 'menu_book',
        request: `Opened the classification guide${guide ? ` for ${guide}` : ''}`,
        response: errorResponse ?? (text ? 'Guide received' : 'Empty guide'),
        tone,
      }
    }
    case 'view_page_image': {
      const page = Number(input.page)
      return {
        icon: 'image',
        request: `Looked at the image of page ${Number.isFinite(page) ? page : '?'}`,
        response: errorResponse ?? 'Page image received',
        tone,
        pages: Number.isFinite(page) ? [page] : [],
      }
    }
    case 'submit_result': {
      const category = [input.phase, input.outcome, input.reason].map(humanize).filter(Boolean)
      const codes = countCodes(input)
      return {
        icon: 'task_alt',
        request: `Submitted the classification: ${category.join(' · ') || 'no category'}, ${codes} ${pluralize(codes, 'code')}`,
        response:
          errorResponse ??
          (result ? `Accepted${text.includes('arning') ? ' with warnings' : ''}` : null),
        tone: isError ? 'error' : result ? 'success' : undefined,
      }
    }
    case 'get_patient_matches': {
      const criteria = Object.entries(input)
        .filter(([, value]) => value !== null && value !== '' && value !== false)
        .filter(([, value]) => !(Array.isArray(value) && value.length === 0))
        .map(([key]) => MATCH_CRITERIA_LABELS[key] ?? humanize(key))
      const rows = [...text.matchAll(/^\s*([CP]\d+):/gm)].map((match) => match[1])
      const claims = rows.filter((handle) => handle.startsWith('C')).length
      const pas = rows.filter((handle) => handle.startsWith('P')).length
      const found = [
        claims ? `${claims} claim ${pluralize(claims, 'case')}` : null,
        pas ? `${pas} PA ${pluralize(pas, 'case')}` : null,
      ].filter(Boolean)
      return {
        icon: 'person_search',
        request: `Looked up patient cases by ${criteria.slice(0, 5).join(', ')}${criteria.length > 5 ? ` and ${criteria.length - 5} more` : ''}`,
        response:
          errorResponse ??
          (found.length ? `Found ${found.join(' and ')}` : firstLine(text) || 'No cases found'),
        tone,
      }
    }
    case 'submit_patient_match': {
      const picks = [describePick('Claim', input.claim), describePick('PA', input.pa)].filter(
        Boolean,
      )
      return {
        icon: 'how_to_reg',
        request: `Submitted the patient match${picks.length ? ` — ${picks.join(' · ')}` : ''}`,
        response: errorResponse ?? (result ? 'Accepted' : null),
        tone: isError ? 'error' : result ? 'success' : undefined,
      }
    }
    default:
      return {
        icon: 'build',
        request: `Called ${name}`,
        response: errorResponse ?? (text ? firstLine(text) : null),
        tone,
      }
  }
}

/** The transcript as a list of steps, in order: each tool request paired with its result. */
export function buildAgentSteps(transcript: AgentTranscript): AgentStep[] {
  const messages = transcript.messages ?? []
  const calls = transcript.calls ?? []
  const results = new Map<string, ToolResultBlock>()
  for (const message of messages) {
    for (const block of blocksOf(message)) {
      if (isToolResultBlock(block)) results.set(block.tool_use_id, block)
    }
  }

  const steps: AgentStep[] = []
  let callIndex = 0
  let elapsed = 0
  let hasLatency = false

  messages.forEach((message, messageIndex) => {
    const blocks = blocksOf(message)

    if (message.role === 'user') {
      const documentRef = blocks.find((block) => block.type === 'document_ref')
      if (documentRef && documentRef.type === 'document_ref') {
        const pages = typeof documentRef.pages === 'number' ? documentRef.pages : null
        steps.push({
          id: `m${messageIndex}`,
          kind: 'document',
          icon: 'inbox',
          request: 'Was given the document and its instructions',
          response: pages === null ? null : `${pages} ${pluralize(pages, 'page')} of text`,
          tone: undefined,
          elapsedMs: hasLatency ? elapsed : null,
          blocks,
        })
        return
      }
      if (!blocks.some(isToolResultBlock)) {
        const text = blocks
          .map((block) => (block.type === 'text' ? block.text : ''))
          .join('\n')
          .trim()
        steps.push({
          id: `m${messageIndex}`,
          kind: 'nudge',
          icon: 'campaign',
          request:
            messageIndex === 0 ? 'Was given its instructions' : 'The harness reminded the agent',
          response: text ? firstLine(text) : null,
          tone: undefined,
          elapsedMs: hasLatency ? elapsed : null,
          blocks,
        })
      }
      return
    }

    const call = calls[callIndex++]
    if (typeof call?.latency_ms === 'number') {
      elapsed += call.latency_ms
      hasLatency = true
    }
    const thinking = blocks
      .map((block) => (block.type === 'thinking' ? block.thinking : ''))
      .filter(Boolean)
      .join('\n\n')
    const text = blocks
      .map((block) => (block.type === 'text' ? block.text : ''))
      .filter(Boolean)
      .join('\n\n')
    const toolUses = blocks.filter((block) => block.type === 'tool_use')

    if (toolUses.length === 0) {
      steps.push({
        id: `m${messageIndex}`,
        kind: 'message',
        icon: 'chat_bubble',
        request: text ? 'Wrote' : 'Replied',
        response: text ? firstLine(text) : null,
        tone: undefined,
        elapsedMs: hasLatency ? elapsed : null,
        thinking: thinking || undefined,
        text: text || undefined,
        call,
        blocks,
      })
      return
    }

    toolUses.forEach((block, toolIndex) => {
      if (block.type !== 'tool_use') return
      const result = results.get(block.id)
      const input = asInput(block.input)
      steps.push({
        id: `m${messageIndex}-${toolIndex}`,
        kind: 'tool',
        ...describeTool(block.name, input, result),
        elapsedMs: hasLatency ? elapsed : null,
        toolName: block.name,
        input: block.input,
        result,
        // The reply's reasoning goes with its first request.
        thinking: toolIndex === 0 ? thinking || undefined : undefined,
        text: toolIndex === 0 ? text || undefined : undefined,
        call: toolIndex === 0 ? call : undefined,
      })
    })
  })

  return steps
}

export function sessionLatency(transcript: AgentTranscript | null): number | null {
  const latencies = (transcript?.calls ?? [])
    .map((call) => call.latency_ms)
    .filter((value): value is number => typeof value === 'number')
  return latencies.length ? latencies.reduce((sum, value) => sum + value, 0) : null
}
